import { z } from "zod";
import { blockSchema } from "./blocks.js";
import { messageDtoSchema } from "./dto/message.js";
import { panelMessageDtoSchema } from "./dto/visitor.js";
import { conversationControlSchema, conversationStatusSchema } from "./dto/conversation.js";
import { themeInputSchema } from "./theme.js";

/**
 * `{ v, id, type, ts, data }` — PLAN.md "Realtime design". `v` is what lets
 * a stale client be *told* it's stale instead of crashing on an unknown
 * field; it stays at 1 until the envelope shape itself changes, which is
 * independent of adding new `type`s.
 */
export const wsEnvelopeSchema = z.object({
  v: z.literal(1),
  id: z.string(),
  type: z.string(),
  ts: z.number(),
  data: z.unknown(),
});
export type WsEnvelope<T = unknown> = { v: 1; id: string; type: string; ts: number; data: T };

export function makeEnvelope<T>(type: string, data: T): WsEnvelope<T> {
  return { v: 1, id: crypto.randomUUID(), type, ts: Date.now(), data };
}

// --- Widget <-> server ------------------------------------------------
// M2 implements: session.ready, msg.send/ack/delta/complete/new, resume,
// session.reset, ping/pong, error. tool.start/tool.end land with the M3
// tool loop; typing/proactive/feedback.submit/order.confirm/action.invoke/
// page.view land with their owning milestones (M6-M8) — the envelope and
// message names are fixed by PLAN.md now so those milestones are additive.

/**
 * `pageUrl`/`pageTitle` are the page the shopper was on when they hit send
 * — the difference between "asked about delivery" and "asked about delivery
 * on the checkout page" (PLAN.md "Client panel"). Country is *not* here and
 * never will be: it's resolved server-side from a connection address that is
 * then dropped, because anything the client sends is something the client
 * can lie about.
 */
export const msgSendSchema = z
  .object({
    clientMsgId: z.string(),
    /**
     * Empty is allowed only alongside an attachment — a photo sent with no
     * caption is a message; an empty box is not. Both travel in one
     * `msg.send` rather than as a file event followed by a text event,
     * because "here is a picture of the part, does it fit?" is one thing
     * the shopper said and has to reach the model as one turn.
     */
    text: z.string().max(8000).default(""),
    /** Ids from `POST /api/widget/upload`, already stored and processed. */
    attachmentIds: z.array(z.string().uuid()).max(6).default([]),
    pageUrl: z.string().max(2048).optional(),
    pageTitle: z.string().max(512).optional(),
  })
  .refine((value) => value.text.trim().length > 0 || value.attachmentIds.length > 0, {
    message: "a message needs text, an attachment, or both",
    path: ["text"],
  });
export type MsgSend = z.infer<typeof msgSendSchema>;

/** Sent on load and on every SPA navigation (`pushState`/`replaceState`/`popstate`) — PLAN.md "Session & visitor tracking". */
export const pageViewSchema = z.object({
  url: z.string().max(2048),
  title: z.string().max(512).optional(),
  referrer: z.string().max(2048).optional(),
  language: z.string().max(35).optional(),
  isSpaNav: z.boolean().default(false),
});
export type PageView = z.infer<typeof pageViewSchema>;

export const resumeSchema = z.object({ lastSeq: z.number().int().nonnegative() });
export type Resume = z.infer<typeof resumeSchema>;

export const sessionReadySchema = z.object({
  conversationId: z.string().uuid(),
  status: conversationStatusSchema,
  control: conversationControlSchema,
  /**
   * `allowImageUploads` is a *capability*, not config: the widget needs to
   * know whether to offer an attach button, and that is the only thing
   * about the bot's configuration it is ever told (PLAN.md test 24 — the
   * prompt and tool list never cross this wire).
   */
  bot: z.object({ name: z.string(), greeting: z.string().optional(), allowImageUploads: z.boolean().default(false) }),
  theme: themeInputSchema,
  messages: z.array(messageDtoSchema),
  /**
   * A fresh session token, sent only when the conversation changed under
   * an open socket ("Start a new conversation"). The token names its
   * conversation, so uploads, image URLs and reconnects made with the old
   * one would all still land in the conversation the shopper just left.
   */
  token: z.string().optional(),
});
export type SessionReady = z.infer<typeof sessionReadySchema>;

export const msgAckSchema = z.object({ clientMsgId: z.string(), seq: z.number().int().nonnegative() });
export type MsgAck = z.infer<typeof msgAckSchema>;

/** No `seq` yet — a delta streams before its message is persisted; `msg.complete` carries the real one once the row exists. */
export const msgDeltaSchema = z.object({ messageId: z.string().uuid(), delta: z.string() });
export type MsgDelta = z.infer<typeof msgDeltaSchema>;

/**
 * `content` is the served reply in full, not a delta. Deltas are the fast
 * path, not the only one: a reply that never streamed — the canned message
 * after a provider failure, or a budget block — produces no deltas at all,
 * and a widget with only `blocks` to go on rendered an empty bubble until
 * the page was reloaded. Optional because an older widget build ignores it
 * and rebuilds the text from its deltas exactly as before.
 */
export const msgCompleteSchema = z.object({
  messageId: z.string().uuid(),
  seq: z.number().int().nonnegative(),
  content: z.string().nullable().optional(),
  blocks: z.array(blockSchema),
});
export type MsgComplete = z.infer<typeof msgCompleteSchema>;

export const errorMessageSchema = z.object({ code: z.string(), message: z.string().optional() });
export type ErrorMessage = z.infer<typeof errorMessageSchema>;

/**
 * The visitor's explicit Confirm — the one thing that turns a draft into a
 * send (PLAN.md "Orders never send on the model's say-so"). The widget sends
 * only the order id: everything about what is being confirmed already lives
 * server-side, repriced, so nothing here can alter it.
 */
export const orderConfirmSchema = z.object({ orderId: z.string().uuid() });
export type OrderConfirm = z.infer<typeof orderConfirmSchema>;

/** Thumbs on an assistant message, or a rating for the conversation as a whole. */
export const feedbackSubmitSchema = z.object({
  messageId: z.string().uuid().optional(),
  rating: z.number().int().min(-1).max(5),
  comment: z.string().max(2000).optional(),
});
export type FeedbackSubmit = z.infer<typeof feedbackSubmitSchema>;

/** Server → widget once a confirmed order has actually left the building (or failed to). */
export const orderStatusEventSchema = z.object({
  orderId: z.string().uuid(),
  status: z.enum(["confirmed", "submitted", "failed"]),
  message: z.string().optional(),
});
export type OrderStatusEvent = z.infer<typeof orderStatusEventSchema>;

/**
 * Server → widget when a human takes over or hands back, so the widget can
 * say *that* a person is replying.
 *
 * `agentName` is kept only so an older widget build still parses the
 * frame; the server no longer sets it. It used to carry the agent's login
 * email, which then sat in the header of a client's public site — the
 * shopper is told a person is answering, never which one.
 */
export const statusChangeSchema = z.object({
  status: conversationStatusSchema,
  control: conversationControlSchema,
  agentName: z.string().optional(),
});
export type StatusChange = z.infer<typeof statusChangeSchema>;

/** Both directions: the widget tells the panel a shopper is typing, and the panel tells the widget an agent is. */
export const typingSchema = z.object({ isTyping: z.boolean() });
export type Typing = z.infer<typeof typingSchema>;

// --- Panel/app <-> server ---------------------------------------------

export const convSubscribeSchema = z.object({ conversationId: z.string().uuid() });
export type ConvSubscribe = z.infer<typeof convSubscribeSchema>;

/** Same "text, an attachment, or both" rule as the widget's `msg.send` — an agent answering "is this the right part?" replies with the photo. */
export const convSendSchema = z
  .object({
    conversationId: z.string().uuid(),
    text: z.string().max(8000).default(""),
    attachmentIds: z.array(z.string().uuid()).max(6).default([]),
    clientMsgId: z.string().optional(),
  })
  .refine((value) => value.text.trim().length > 0 || value.attachmentIds.length > 0, {
    message: "a message needs text, an attachment, or both",
    path: ["text"],
  });
export type ConvSend = z.infer<typeof convSendSchema>;

export const convAssignSchema = z.object({ conversationId: z.string().uuid(), userId: z.string().uuid() });
export type ConvAssign = z.infer<typeof convAssignSchema>;

export const convCloseSchema = z.object({ conversationId: z.string().uuid(), reason: z.string().max(200).default("agent_closed") });
export type ConvClose = z.infer<typeof convCloseSchema>;

export const convTypingSchema = z.object({ conversationId: z.string().uuid(), isTyping: z.boolean() });
export type ConvTyping = z.infer<typeof convTypingSchema>;

/**
 * Panel → server takeover. `force` is the explicit "take it from them":
 * without it, a conversation another agent holds stays theirs and the
 * request is refused — two agents answering one shopper at once is the
 * collision this exists to prevent, so it has to be a deliberate act.
 */
export const convTakeoverSchema = z.object({ conversationId: z.string().uuid(), force: z.boolean().default(false) });
export type ConvTakeover = z.infer<typeof convTakeoverSchema>;

/**
 * Server → panel on `conv:{id}`. `user` is set only when an agent is the
 * one typing, so a second agent sees *who* is mid-reply — the widget's
 * relay drops it, and the shopper only ever learns that someone is.
 */
export const convTypingEventSchema = z.object({
  conversationId: z.string().uuid(),
  isTyping: z.boolean(),
  from: z.enum(["visitor", "agent", "bot"]),
  user: z.object({ id: z.string().uuid(), name: z.string() }).optional(),
});
export type ConvTypingEvent = z.infer<typeof convTypingEventSchema>;

/** Server → panel: one inbox row changed. Carries the row itself, so an open inbox re-renders without a round trip. */
export const inboxUpsertSchema = z.object({
  conversationId: z.string().uuid(),
  status: conversationStatusSchema,
  control: conversationControlSchema,
  assignedUserId: z.string().uuid().nullable(),
  lastMessagePreview: z.string().nullable(),
  lastMessageAt: z.string(),
  /**
   * Who wrote the message this upsert is about; null when the upsert is a
   * status/control change with no new message (takeover, release, close).
   * Lets a listener decide whether this is "a human needs to look" (a
   * visitor wrote in) versus routine bot chatter or its own outgoing send.
   */
  lastMessageRole: z.enum(["user", "assistant", "agent"]).nullable().default(null),
});
export type InboxUpsert = z.infer<typeof inboxUpsertSchema>;

/**
 * Server → panel: a crawl moved.
 *
 * Declared here rather than published ad hoc because a crawl is the one
 * background job an admin sits and watches, and "it says 12 pages, is it
 * stuck?" is answerable only if the counters mean the same thing to the
 * server and the page. Sent on a throttle, not per page.
 */
export const knowledgeCrawlProgressSchema = z.object({
  crawlId: z.string().uuid(),
  sourceId: z.string().uuid(),
  status: z.enum(["queued", "running", "succeeded", "partial", "failed", "cancelled"]),
  /** Set only once the crawl has stopped — it is the answer to "why did it stop there". */
  stopReason: z.enum(["completed", "max_pages", "max_duration", "budget_exhausted", "circuit_open"]).nullable().default(null),
  pagesFetched: z.number().int().nonnegative(),
  pagesUnchanged: z.number().int().nonnegative(),
  pagesSkippedByRule: z.number().int().nonnegative(),
  pagesFailed: z.number().int().nonnegative(),
  chunksWritten: z.number().int().nonnegative(),
  documentsRemoved: z.number().int().nonnegative(),
  costUsd: z.number().nonnegative(),
  /** Present on the last event of a failed crawl, so the panel can show the reason without refetching. */
  error: z.string().nullable().default(null),
});
export type KnowledgeCrawlProgress = z.infer<typeof knowledgeCrawlProgressSchema>;

export const escalationNewSchema = z.object({
  conversationId: z.string().uuid(),
  reason: z.string(),
  summary: z.string().nullable(),
  requestedAt: z.string(),
});
export type EscalationNew = z.infer<typeof escalationNewSchema>;

/** Server → panel: a message landed in a conversation the panel is subscribed to. */
/**
 * Panel-only, so it carries the panel's own message shape rather than the
 * widget's: the transcript shows tool calls, and a payload typed as the
 * visitor-facing DTO could never include one. Being built by the same
 * mapper the page load uses also means a live-arriving message and a
 * reloaded one can't drift apart.
 */
export const convMessageSchema = z.object({
  conversationId: z.string().uuid(),
  message: panelMessageDtoSchema,
});
export type ConvMessage = z.infer<typeof convMessageSchema>;

export const convStatusSchema = z.object({
  conversationId: z.string().uuid(),
  status: conversationStatusSchema,
  control: conversationControlSchema,
  assignedUserId: z.string().uuid().nullable(),
  agentName: z.string().optional(),
});
export type ConvStatus = z.infer<typeof convStatusSchema>;

/**
 * Published on `conv:{id}` to kill an in-flight generation the instant a
 * human takes over. This is the **fast** path only: the generating replica
 * may never see it, so `runTurn` also re-reads `control` from Postgres and
 * stops there. A dropped abort costs one already-generated chunk; it must
 * never cost a bot talking over a live agent (PLAN.md "The abort must
 * degrade, because it rides on pub/sub").
 */
export const turnAbortSchema = z.object({ conversationId: z.string().uuid() });
export type TurnAbort = z.infer<typeof turnAbortSchema>;

/**
 * Published on `org:{orgId}` when a profile regenerates, so an open pane
 * re-renders without polling (PLAN.md "Updates arrive live"). The payload
 * carries the ids, not the prose: the panel re-fetches through the same
 * mapper the page load used, so there's one shape of profile on the wire.
 */
export const visitorProfileUpdatedSchema = z.object({
  visitorId: z.string().uuid(),
  generatedAt: z.string(),
});
export type VisitorProfileUpdated = z.infer<typeof visitorProfileUpdatedSchema>;

/**
 * A labelled form the visitor filled in — contact capture today, order
 * details later. Fields go straight to `visitor_facts` with no model in the
 * path: a value typed into a labelled box needs no extraction, and routing
 * it through one would be a way to lose it (PLAN.md "AI visitor summary").
 */
export const formSubmitSchema = z.object({
  fields: z.record(z.string().max(500)),
});
export type FormSubmit = z.infer<typeof formSubmitSchema>;

/**
 * A widget analytics event, relayed to the platform's product analytics
 * rather than sent from the shopper's browser to a third party.
 *
 * Why through the socket at all, when PostHog has a perfectly good browser
 * SDK: the widget runs inside *someone else's* page. A direct capture call
 * would need the client site's `connect-src` to allow a host they never
 * agreed to (a strict CSP simply drops it, silently, on exactly the
 * best-run sites), and it would cost the bundle more than its whole gzip
 * budget (apps/widget/scripts/check-size.mjs).
 *
 * The frame therefore carries only what the browser is the sole witness to.
 * Everything identifying — org, bot, visitor — is attached server-side from
 * the session's own claims, because a frame off a shopper's socket is a
 * claim, not a fact. `name` is checked against `RELAYABLE_WIDGET_EVENTS`
 * for the same reason.
 */
export const analyticsTrackSchema = z.object({
  name: z.string().max(64),
  /** Shape, not content: counts, durations, positions. Never message text. */
  properties: z.record(z.union([z.string().max(200), z.number(), z.boolean()])).default({}),
});
export type AnalyticsTrack = z.infer<typeof analyticsTrackSchema>;

/** Widget -> server envelope `type`s implemented as of M8. */
export const WIDGET_TO_SERVER_TYPES = [
  "msg.send",
  "session.reset",
  "resume",
  "ping",
  "page.view",
  "typing",
  "order.confirm",
  "feedback.submit",
  "form.submit",
  "analytics.track",
] as const;
/** Server -> widget envelope `type`s implemented as of M7. */
export const SERVER_TO_WIDGET_TYPES = [
  "session.ready",
  "msg.ack",
  "msg.delta",
  "msg.complete",
  "msg.new",
  "status.change",
  "typing",
  "order.status",
  "error",
  "pong",
] as const;
/** Panel/app -> server envelope `type`s implemented as of M7. */
export const PANEL_TO_SERVER_TYPES = [
  "conv.subscribe",
  "conv.unsubscribe",
  "conv.send",
  "conv.takeover",
  "conv.release",
  "conv.assign",
  "conv.close",
  "typing",
  "presence.set",
  "ping",
] as const;
/** Server -> panel/app envelope `type`s implemented as of M7. */
export const SERVER_TO_PANEL_TYPES = [
  "inbox.upsert",
  "escalation.new",
  "conv.message",
  "conv.typing",
  "conv.status",
  "visitor.profile.updated",
  "knowledge.crawl.progress",
  "presence.update",
  "error",
  "pong",
] as const;
